'use client'

import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowRight, Leaf, Award, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ImageWithFallback } from './ImageWithFallback'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

export function ModernHero() {
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 500], [0, 150])
  const opacity = useTransform(scrollY, [0, 300], [1, 0])
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en

  const features = [
    { icon: Leaf, text: copy.hero.features[0] },
    { icon: Award, text: copy.hero.features[1] },
    { icon: MapPin, text: copy.hero.features[2] },
  ]

  return (
    <section id="home" className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-br from-accent/20 via-background to-background dark:from-primary/10 dark:via-background dark:to-background">
      <motion.div className="absolute inset-0" style={{ y }}>
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-background/55 via-background/35 to-background dark:from-background/90 dark:via-background/65 dark:to-background" />
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-background/80 via-background/25 to-transparent dark:from-background/95 dark:via-background/55 dark:to-background/20" />
        <ImageWithFallback
          src="/hero-section.jpg"
          alt="Olive trees"
          className="h-full w-full object-cover opacity-95 dark:opacity-70"
        />
      </motion.div>

      <motion.div className="relative z-20 mx-auto max-w-7xl px-4 py-32 sm:px-6 sm:py-40" style={{ opacity }}>
        <div className="max-w-3xl">
          <motion.div className="mb-8 inline-flex items-center space-x-2 rounded-full bg-primary px-4 py-2 text-primary-foreground shadow-lg shadow-black/20 dark:bg-primary/90 dark:shadow-primary/20" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <Leaf className="h-4 w-4 text-primary-foreground" />
            <span className="text-sm text-primary-foreground">{copy.hero.badge}</span>
          </motion.div>

          <motion.h1 className="mb-6 text-5xl tracking-tight sm:text-7xl lg:text-8xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
            {copy.hero.titleLine1}
            <br />
            <span className="text-primary dark:text-primary-foreground">{copy.hero.titleLine2}</span>
          </motion.h1>

          <motion.p className="mb-12 max-w-2xl text-xl leading-relaxed text-foreground/70 dark:text-foreground/82" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
            {copy.hero.subtitle}
          </motion.p>

          <motion.div className="mb-16 flex flex-col gap-4 sm:flex-row" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}>
            <Button size="lg" className="group rounded-full bg-primary px-8 py-6 text-primary-foreground hover:bg-primary/90" asChild>
              <a href="/boutique">
                {copy.hero.ctaPrimary}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-2 border-primary/20 bg-background/60 px-8 py-6 hover:border-primary/40 hover:bg-primary/5 dark:border-primary-foreground/25 dark:bg-background/75 dark:hover:bg-background/85" asChild>
              <a href="#story">{copy.hero.ctaSecondary}</a>
            </Button>
          </motion.div>

          <motion.div className="flex flex-wrap gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
            {features.map((feature) => (
              <motion.div key={feature.text} className="flex items-center space-x-2 rounded-full border border-border bg-card/80 px-5 py-3 backdrop-blur-sm dark:border-primary-foreground/15 dark:bg-background/70" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                <feature.icon className="h-4 w-4 text-primary dark:text-primary-foreground" />
                <span className="text-sm text-foreground/80 dark:text-foreground/88">{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }}>
        <motion.div className="h-10 w-6 rounded-full border-2 border-primary/30 p-1 dark:border-primary-foreground/40" animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <div className="mx-auto h-2 w-1.5 rounded-full bg-primary dark:bg-primary-foreground" />
        </motion.div>
      </motion.div>
    </section>
  )
}
